/**
 * Promo Details Drawer — manages the sidebar modal for multi-promo display.
 * Follows the account-drawer initialization and state management pattern.
 */

import { disableScrolling, enableScrolling } from './utils';
import { openModal } from './modal';

function formatText(input, maxChars = 20, maxLines = 3) {
  const words = input.split(' ');
  const lines: string[] = [];
  let currentLine = '';

  for (let word of words) {
    // If adding the word exceeds the limit
    if ((currentLine + ' ' + word).trim().length > maxChars) {
      lines.push(currentLine.trim());
      currentLine = word;

      // Stop if we hit max lines
      if (lines.length === maxLines) {
        break;
      }
    } else {
      currentLine += ' ' + word;
    }
  }

  // Add the last line if there's space
  if (lines.length < maxLines && currentLine) {
    lines.push(currentLine.trim());
  }

  let result = lines.slice(0, maxLines);

  // If we didn't include all words, truncate with "..."
  const usedText = result.join(' ');
  if (usedText.length < input.length) {
    result[maxLines - 1] = result[maxLines - 1].replace(/\s+$/, '') + '...';
  }

  return result.join('\n');
}

// ---------------------------------------------------------------------------
// Types (mirrors Promotion model fields returned by /api/products/promotions)
// ---------------------------------------------------------------------------

interface PromoFilterBase {
  priceOverrideType: 'dollar' | 'percent' | 'none';
  priceOverrideAmount: number | null;
}

interface ProductFilter extends PromoFilterBase {
  productId: number;
  uom: string | null;
}

interface BrandFilter extends PromoFilterBase {
  brandId: number;
}

interface SupplierFilter extends PromoFilterBase {
  supplierId: number;
}

interface ProductGroupFilter extends PromoFilterBase {
  path: string;
}

interface SeriesFilter extends PromoFilterBase {
  seriesId: number;
}

export interface PromoLayerProduct {
  id: number;
  name: string;
  sku: string;
  imageUrl: string;
  price: { base: number; discount: number | null; uom: { label: string } };
  quantityBreaks?: { base: number; discount: number | null; uom: { code: string; label: string } }[];
}

export interface PromoLayer {
  id: number;
  type: 'purchase' | 'spend' | 'receive';
  layerOrder: number;
  quantity: number | null;
  quantityType?: 'count' | 'dollar' | 'rebate' | 'none';
  productFilters: ProductFilter[];
  brandFilters: BrandFilter[];
  supplierFilters: SupplierFilter[];
  productGroupFilters: ProductGroupFilter[];
  seriesFilters: SeriesFilter[];
  /** Present when fetched with include_products=true. */
  products?: PromoLayerProduct[];
}

export interface PromoApiObject {
  id: number;
  name: string;
  slug: string;
  description: string | null;
  imageUrl: string | null;
  isTieredPromotion: boolean;
  tierName: string;
  tierPromoIds: number[];
  /** Display order; lower = higher priority. null sorts last. */
  sortOrder?: number | null;
  startsAt?: string | Date | null;
  endsAt?: string | Date | null;
  promoType: {
    name: string;
    slug: string;
    description: string | null;
  };
  layers: PromoLayer[];
}

// ---------------------------------------------------------------------------
// Drawer renderers
// ---------------------------------------------------------------------------

function renderPromoDrawerImageItem(promo: PromoApiObject): HTMLElement {
  const item = document.createElement('button');
  item.setAttribute('type', 'button');
  item.classList.add('promo-drawer-image-item');
  item.dataset.promoId = String(promo.id);
  item.setAttribute('aria-label', `View details for ${promo.name}`);

  const image = document.createElement('img');
  image.classList.add('promo-drawer-image-item__image', 'lazyload');
  image.alt = promo.name;
  image.dataset.src = promo.imageUrl || `https://placehold.co/900x300?font=montserrat&text=${encodeURIComponent(formatText(promo.name))}`;

  item.appendChild(image);

  return item;
}

/**
 * Order promos for drawer display: by sortOrder ascending (null/undefined last),
 * then by slug ascending as a tiebreak.
 *
 * Tier-chain collapse (show only the entry tier) is done server-side in
 * `ShopController.renderProduct` — the multi-promo drawer's ids arrive already collapsed. We
 * deliberately do NOT collapse here: this drawer is also opened for a single promo from the
 * cart's "View promo" link, where the promo is the *active* tier (often a non-first tier) —
 * collapsing on `tierPromoIds[0]` would wrongly blank that drawer.
 */
function sortDrawerPromotions(promotions: PromoApiObject[]): PromoApiObject[] {
  return [...promotions].sort((a, b) => {
    const aOrder = a.sortOrder ?? Number.POSITIVE_INFINITY;
    const bOrder = b.sortOrder ?? Number.POSITIVE_INFINITY;
    if (aOrder !== bOrder) {
      return aOrder - bOrder;
    }
    return a.slug.localeCompare(b.slug);
  });
}

/**
 * Get HTML string of all promo items for drawer display.
 * Used when rendering multiple promos in the sidebar drawer.
 */
export function getPromoDrawerContent(promotions: PromoApiObject[]): string {
  const container = document.createElement('div');
  container.classList.add('promo-list');

  sortDrawerPromotions(promotions).forEach((promo) => {
    container.appendChild(renderPromoDrawerImageItem(promo));
  });

  return container.innerHTML;
}

/**
 * Fetch SSR modal content for a single promo and open the promo detail modal.
 */
async function loadAndOpenPromoModal(promoId: string, trigger: HTMLElement) {
  try {
    const res = await fetch(`/api/products/promotions/render-modal?id=${encodeURIComponent(promoId)}`);
    const content = document.querySelector('.js-promo-detail-content');
    if (content) {
      if (res.ok) {
        const { html } = await res.json();
        const fallback = '<p class="promo-detail__empty-message">Promo details are unavailable right now.</p>';
        // The "View qualifying products" action is part of the rendered content now (set
        // server-side, omitted for combo promos) — no need to patch a link href here.
        content.innerHTML = html || fallback;
      } else {
        content.innerHTML = '<p class="promo-detail__empty-message">Promo details are unavailable right now.</p>';
      }
    }
    openModal('promo-detail', trigger);
  } catch (err) {
    console.error('Error loading promo detail:', err);
  }
}

/** DOM reference */
const promoDetailsDrawer = document.getElementById('js-promo-details-drawer');

/** Internal state (safe defaults) */
let lastFocusedElement: HTMLElement | null = null;

/**
 * Initialize ONLY if promo details drawer exists
 */
if (promoDetailsDrawer) {
  initPromoDetailsDrawer(promoDetailsDrawer);
}

/**
 * Init logic (runs once per page)
 */
function initPromoDetailsDrawer(drawer: HTMLElement) {
  const closeBtns = drawer.querySelectorAll('.js-close-btn');
  const drawerOverlay = drawer.querySelector('.promo-details-drawer__overlay');

  // Use delegation to support async-rendered trigger buttons.
  // If the button carries data-promo-ids, fetch those promos and refresh the drawer content first.
  document.addEventListener('click', async (e) => {
    const target = e.target as HTMLElement;
    const toggle = target.closest<HTMLElement>('.js-toggle-promo-details');
    if (!toggle) {
      return;
    }
    e.preventDefault();
    const promoIds = toggle.dataset.promoIds;
    if (promoIds) {
      try {
        const params = promoIds.split(',').map((id) => `id=${encodeURIComponent(id)}`).join('&');
        const res = await fetch(`/api/products/promotions?${params}`);
        if (res.ok) {
          const promos = await res.json();
          setPromoContent(getPromoDrawerContent(promos));
        }
      } catch (err) {
        console.error('Error loading promo details:', err);
      }
    }
    openPromoDetailsDrawer();
  });

  // Open the promo detail modal when a drawer image item is clicked.
  document.addEventListener('click', async (e) => {
    const item = (e.target as HTMLElement).closest<HTMLElement>('.promo-drawer-image-item');
    if (!item?.dataset.promoId) {
      return;
    }
    await loadAndOpenPromoModal(item.dataset.promoId, item);
  });

  // Open the promo detail modal when a "View details" button on a PDP promo callout is clicked.
  document.addEventListener('click', async (e) => {
    const btn = (e.target as HTMLElement).closest<HTMLElement>('.js-view-promo-detail');
    if (!btn?.dataset.promoId) {
      return;
    }
    await loadAndOpenPromoModal(btn.dataset.promoId, btn);
  });

  closeBtns.forEach((btn) => btn.addEventListener('click', closePromoDetailsDrawer));
  drawerOverlay?.addEventListener('click', closePromoDetailsDrawer);

  document.addEventListener('keyup', (e) => {
    if (e.key === 'Escape' && drawer.classList.contains('open')) {
      e.preventDefault();
      closePromoDetailsDrawer();
    }
  });
}

/**
 * Open promo details drawer
 */
export function openPromoDetailsDrawer() {
  if (!promoDetailsDrawer) {
    return;
  }

  lastFocusedElement = document.activeElement as HTMLElement;
  promoDetailsDrawer.classList.add('open');
  promoDetailsDrawer.removeAttribute('aria-hidden');
  promoDetailsDrawer.removeAttribute('inert');
  disableScrolling();

  promoDetailsDrawer.focus({ preventScroll: true });
}

/**
 * Close promo details drawer
 */
export async function closePromoDetailsDrawer() {
  if (!promoDetailsDrawer) {
    return;
  }

  promoDetailsDrawer.classList.remove('open');
  promoDetailsDrawer.setAttribute('inert', '');
  promoDetailsDrawer.setAttribute('aria-hidden', 'true');
  enableScrolling();

  if (lastFocusedElement) {
    lastFocusedElement.focus({ preventScroll: true });
    lastFocusedElement = null;
  }
}

/**
 * Populate the drawer with rendered promo items (HTML).
 * Expects HTML string of promo items (see getPromoDrawerContent).
 */
export function setPromoContent(html: string) {
  if (!promoDetailsDrawer) {
    return;
  }
  const content = promoDetailsDrawer.querySelector('.js-promo-details-content');
  if (content) {
    content.innerHTML = html;
  }
}

